import React, { forwardRef, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { mergeRefs } from '@utils/mergeRefs';
import styles from './DisclosurePopover.module.css';

export interface IDisclosurePopoverProps extends Omit<
  React.HTMLAttributes<HTMLSpanElement>,
  'title'
> {
  /** Link text that opens the popover. */
  label: string;
  title: string;
  children: React.ReactNode;
}

const PANEL_GAP = 8;
const PANEL_MARGIN = 16;

const DisclosurePopover = forwardRef<HTMLSpanElement, IDisclosurePopoverProps>(
  ({ label, title, children, className, ...rest }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState<{
      top: number;
      left: number;
      maxHeight: number;
    } | null>(null);
    const popoverId = useId();
    const titleId = useId();
    const rootRef = useRef<HTMLSpanElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);

    // Portalled with fixed coordinates: the trigger sits inside cards that clip their overflow.
    useLayoutEffect(() => {
      if (!isOpen) return undefined;
      const place = () => {
        const trigger = triggerRef.current?.getBoundingClientRect();
        if (!trigger) return;
        const width = panelRef.current?.offsetWidth ?? 0;
        const height = panelRef.current?.scrollHeight ?? 0;
        const maxLeft = window.innerWidth - width - PANEL_MARGIN;
        const below = window.innerHeight - trigger.bottom - PANEL_GAP - PANEL_MARGIN;
        const above = trigger.top - PANEL_GAP - PANEL_MARGIN;
        const flip = height > below && above > below;
        const maxHeight = flip ? above : below;
        setPosition({
          top: flip
            ? trigger.top - PANEL_GAP - Math.min(height, above)
            : trigger.bottom + PANEL_GAP,
          left: Math.max(PANEL_MARGIN, Math.min(trigger.left, maxLeft)),
          maxHeight,
        });
      };
      place();
      window.addEventListener('resize', place);
      window.addEventListener('scroll', place, true);
      return () => {
        window.removeEventListener('resize', place);
        window.removeEventListener('scroll', place, true);
      };
    }, [isOpen]);

    useEffect(() => {
      if (!isOpen) return undefined;
      panelRef.current?.focus();

      const close = (returnFocus: boolean) => {
        setIsOpen(false);
        if (returnFocus) triggerRef.current?.focus();
      };
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') close(true);
      };
      const handlePointerDown = (event: PointerEvent) => {
        const target = event.target as Node;
        if (!rootRef.current?.contains(target) && !panelRef.current?.contains(target)) close(false);
      };
      document.addEventListener('keydown', handleKeyDown);
      document.addEventListener('pointerdown', handlePointerDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.removeEventListener('pointerdown', handlePointerDown);
      };
    }, [isOpen]);

    return (
      <span {...rest} ref={mergeRefs(rootRef, ref)} className={`${styles.root} ${className ?? ''}`}>
        <button
          ref={triggerRef}
          type="button"
          className={styles.trigger}
          aria-expanded={isOpen}
          aria-controls={popoverId}
          onClick={() => setIsOpen((open) => !open)}
        >
          {label}
        </button>
        {isOpen &&
          createPortal(
            <div
              ref={panelRef}
              id={popoverId}
              role="dialog"
              aria-labelledby={titleId}
              tabIndex={-1}
              className={styles.panel}
              style={position ?? { visibility: 'hidden' }}
            >
              <div className={styles.header}>
                <h3 id={titleId} className={styles.title}>
                  {title}
                </h3>
                <button
                  type="button"
                  className={styles.close}
                  aria-label="Close"
                  onClick={() => {
                    setIsOpen(false);
                    triggerRef.current?.focus();
                  }}
                >
                  <X size={16} strokeWidth={2} aria-hidden="true" />
                </button>
              </div>
              <div className={styles.body}>{children}</div>
            </div>,
            document.body,
          )}
      </span>
    );
  },
);

DisclosurePopover.displayName = 'DisclosurePopover';

export default DisclosurePopover;
